import {
  RICH_TEXT_MAX_LIST_DEPTH,
  RICH_TEXT_MAX_NODES,
  type RichTextAlign,
  type RichTextBlock,
  type RichTextDoc,
  type RichTextHeading,
  type RichTextInline,
  type RichTextList,
  type RichTextListItem,
  type RichTextMark,
  type RichTextParagraph,
} from './types';

export class RichTextDocError extends Error {
  readonly path: string;

  constructor(message: string, path: string) {
    super(path ? `${message} at ${path}` : message);
    this.name = 'RichTextDocError';
    this.path = path;
  }
}

const SAFE_HREF = /^(?:https?:|mailto:)/i;
const WHITESPACE = /\s/;
const LONE_SURROGATE = /[\uD800-\uDBFF](?![\uDC00-\uDFFF])|(?<![\uD800-\uDBFF])[\uDC00-\uDFFF]/g;
const MARK_ORDER: Record<RichTextMark['type'], number> = {
  bold: 0,
  italic: 1,
  underline: 2,
  strike: 3,
  code: 4,
  link: 5,
};

type Counter = { nodes: number };

const isRecord = (value: unknown): value is Record<string, unknown> =>
  typeof value === 'object' && value !== null && !Array.isArray(value);

function fail(message: string, path: string): never {
  throw new RichTextDocError(message, path);
}

const childPath = (path: string, key: string | number): string => (path ? `${path}.${key}` : String(key));

const unsupportedNode = (type: unknown): string => `Unsupported node type "${String(type)}"`;

const countNode = (counter: Counter, path: string): void => {
  counter.nodes += 1;
  if (counter.nodes > RICH_TEXT_MAX_NODES) fail('Document has too many nodes', path);
};

const readContent = (node: Record<string, unknown>, path: string): unknown[] => {
  if (node.content === undefined) return [];
  if (!Array.isArray(node.content)) fail('Expected content to be an array', childPath(path, 'content'));
  return node.content;
};

const readAlign = (attrs: unknown): RichTextAlign | undefined => {
  if (!isRecord(attrs)) return undefined;
  return attrs.textAlign === 'center' || attrs.textAlign === 'right' ? attrs.textAlign : undefined;
};

const isControlCharacter = (character: string): boolean => {
  const code = character.charCodeAt(0);
  return (code <= 0x1f || (code >= 0x7f && code <= 0x9f)) && !WHITESPACE.test(character);
};

const cleanHref = (href: string): string =>
  Array.from(href.replace(LONE_SURROGATE, ''))
    .filter((character) => !isControlCharacter(character))
    .join('')
    .trim();

const readLink = (attrs: unknown): RichTextMark | null => {
  const href = isRecord(attrs) && typeof attrs.href === 'string' ? cleanHref(attrs.href) : '';
  return SAFE_HREF.test(href) && !WHITESPACE.test(href) ? { type: 'link', attrs: { href } } : null;
};

const readMark = (raw: unknown, path: string): RichTextMark | null => {
  if (!isRecord(raw)) fail('Expected a mark', path);
  const type = raw.type;
  switch (type) {
    case 'bold':
    case 'italic':
    case 'underline':
    case 'strike':
    case 'code':
      return { type };
    case 'link':
      return readLink(raw.attrs);
    default:
      return fail(`Unsupported mark type "${String(type)}"`, path);
  }
};

const readMarks = (raw: unknown, path: string): RichTextMark[] | undefined => {
  if (raw === undefined) return undefined;
  if (!Array.isArray(raw)) fail('Expected marks to be an array', path);
  const byType = new Map<RichTextMark['type'], RichTextMark>();
  raw.forEach((item, index) => {
    const mark = readMark(item, childPath(path, index));
    if (mark && !byType.has(mark.type)) byType.set(mark.type, mark);
  });
  const marks = Array.from(byType.values()).sort((a, b) => MARK_ORDER[a.type] - MARK_ORDER[b.type]);
  return marks.length > 0 ? marks : undefined;
};

const sameMarks = (a: RichTextMark[] | undefined, b: RichTextMark[] | undefined): boolean =>
  JSON.stringify(a) === JSON.stringify(b);

const readInlineContent = (
  node: Record<string, unknown>,
  path: string,
  counter: Counter,
): RichTextInline[] | undefined => {
  const inline: RichTextInline[] = [];
  readContent(node, path).forEach((raw, index) => {
    const nodePath = childPath(childPath(path, 'content'), index);
    if (!isRecord(raw)) fail('Expected a node', nodePath);
    countNode(counter, nodePath);
    if (raw.type === 'hardBreak') {
      inline.push({ type: 'hardBreak' });
      return;
    }
    if (raw.type !== 'text') fail(unsupportedNode(raw.type), nodePath);
    if (typeof raw.text !== 'string') fail('Expected text to be a string', childPath(nodePath, 'text'));
    const text = raw.text.replaceAll('\u0000', '').replace(LONE_SURROGATE, '');
    const marks = readMarks(raw.marks, childPath(nodePath, 'marks'));
    if (!text) return;
    const previous = inline[inline.length - 1];
    if (previous?.type === 'text' && sameMarks(previous.marks, marks)) {
      previous.text += text;
      return;
    }
    inline.push(marks ? { type: 'text', text, marks } : { type: 'text', text });
  });
  return inline.length > 0 ? inline : undefined;
};

const readParagraph = (raw: Record<string, unknown>, path: string, counter: Counter): RichTextParagraph => {
  const textAlign = readAlign(raw.attrs);
  const content = readInlineContent(raw, path, counter);
  return {
    type: 'paragraph',
    ...(textAlign ? { attrs: { textAlign } } : {}),
    ...(content ? { content } : {}),
  };
};

const readHeading = (raw: Record<string, unknown>, path: string, counter: Counter): RichTextHeading => {
  const level = isRecord(raw.attrs) ? raw.attrs.level : undefined;
  if (level !== 1 && level !== 2 && level !== 3)
    fail('Heading level must be 1, 2 or 3', childPath(path, 'attrs.level'));
  const textAlign = readAlign(raw.attrs);
  const content = readInlineContent(raw, path, counter);
  return {
    type: 'heading',
    attrs: textAlign ? { level, textAlign } : { level },
    ...(content ? { content } : {}),
  };
};

const readListItem = (raw: unknown, path: string, listDepth: number, counter: Counter): RichTextListItem => {
  if (!isRecord(raw) || raw.type !== 'listItem') fail('Expected a list item', path);
  countNode(counter, path);
  const children = readContent(raw, path)
    .map((child, index) => readBlock(child, childPath(childPath(path, 'content'), index), listDepth, counter))
    .filter((child): child is RichTextBlock => child !== null);
  return { type: 'listItem', content: children.length > 0 ? children : [{ type: 'paragraph' }] };
};

const readList = (
  raw: Record<string, unknown>,
  type: RichTextList['type'],
  path: string,
  listDepth: number,
  counter: Counter,
): RichTextList | null => {
  if (listDepth >= RICH_TEXT_MAX_LIST_DEPTH) fail('Lists are nested too deeply', path);
  const items = readContent(raw, path).map((item, index) =>
    readListItem(item, childPath(childPath(path, 'content'), index), listDepth + 1, counter),
  );
  return items.length > 0 ? { type, content: items } : null;
};

function readBlock(raw: unknown, path: string, listDepth: number, counter: Counter): RichTextBlock | null {
  if (!isRecord(raw)) fail('Expected a node', path);
  countNode(counter, path);
  const type = raw.type;
  switch (type) {
    case 'paragraph':
      return readParagraph(raw, path, counter);
    case 'heading':
      return readHeading(raw, path, counter);
    case 'bulletList':
    case 'orderedList':
      return readList(raw, type, path, listDepth, counter);
    default:
      return fail(unsupportedNode(type), path);
  }
}

export function parseRichTextDoc(input: unknown): RichTextDoc {
  if (!isRecord(input) || input.type !== 'doc') fail('Expected a rich text document', '');
  const counter: Counter = { nodes: 1 };
  const content = readContent(input, '')
    .map((raw, index) => readBlock(raw, childPath('content', index), 0, counter))
    .filter((block): block is RichTextBlock => block !== null);
  return { type: 'doc', content };
}

export function safeParseRichTextDoc(
  input: unknown,
): { ok: true; doc: RichTextDoc } | { ok: false; error: RichTextDocError } {
  try {
    return { ok: true, doc: parseRichTextDoc(input) };
  } catch (error) {
    if (error instanceof RichTextDocError) return { ok: false, error };
    throw error;
  }
}
